import axios, { AxiosResponse } from "axios";

import {
	getRequest,
	postRequest
} from "@/Utils/Service/BackendService";

import {
	BulkImportTemplateFileType,
	BulkImportResponse
} from "@/Contracts/BulkImport";
import { Opportunity } from "@/Contracts/Opportunity";
import { Product } from "@/Contracts/Products";
import { Client, ClientContact } from "@/Contracts/Clients";
import { AssessmentDataLog } from "@/Contracts/Assessments";
import { HealthStatusDataLog } from "@/Contracts/QBR";
import {
	AssessmentRepresentationStyle,
	QbrRepresentationStyle
} from "@/Contracts/Documents";

const opportunitiesBulkImportUrl = "/v2/opportunities/bulk";
const productsBulkImportUrl = "/v2/items/bulk";
const clientsBulkImportUrl = "/v2/client/companies/bulk";
const clientContactsBulkImportUrl = "/v2/client/users/bulk";
const assessmentsIssuesBulkImportUrl =
	"/v2/assessments/{assessment_id}/logs/bulk";
const qbrIssuesBulkImportUrl = "/v2/qbr/{qbr_id}/logs/bulk";
const opportunitiesBulkImportTemplateDownloadUrl =
	"/v2/opportunities/bulk/template?file_type={file_type}";
const productsBulkImportTemplateDownloadUrl =
	"/v2/items/bulk/template?file_type={file_type}";
const clientsBulkImportTemplateDownloadUrl =
	"/v2/client/companies/bulk/template?file_type={file_type}";
const clientContactsBulkImportTemplateDownloadUrl =
	"/v2/client/users/bulk/template?file_type={file_type}";
const assessmentsIssuesBulkImportTemplateDownloadUrl =
	"/v2/assessments/logs/bulk/template?file_type={file_type}&assessment_representation_style={assessment_representation_style}";
const qbrIssuesBulkImportTemplateDownloadUrl =
	"/v2/qbr/logs/bulk/template?file_type={file_type}&qbr_representation_style={qbr_representation_style}";
const downloadAllProductsCSVURL = "/v2/items/bulk/export";
const downloadAllOpportunitiesCSVURL = "/v2/opportunities/bulk/export/";
const downloadAllClientsCSVURL = "/v2/client/companies/bulk/export";
const downloadAllClientContactsCSVURL = "/v2/client/users/bulk/export";
const downloadAllAssessmentIssuesCSVURL =
	"/v2/assessments/{assessment_id}/logs/export/";
const downloadAllAssessmentMS365IssuesCSVURL =
	"/v2/assessments/{assessment_id}/logs/export/?cloud_assessment_type=ms365";
const downloadAllQbrIssuesCSVURL = "/v2/qbr/{qbr_id}/logs/export/";

export const opportunitiesBulkImport = async (
	formData: FormData
): Promise<BulkImportResponse<Opportunity>> => {
	try {
		const response: AxiosResponse<BulkImportResponse<Opportunity>> =
			await axios(
				postRequest(
					opportunitiesBulkImportUrl,
					formData,
					true,
					false,
					true
				)
			);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const productsBulkImport = async (
	formData: FormData
): Promise<BulkImportResponse<Product>> => {
	try {
		const response: AxiosResponse<BulkImportResponse<Product>> =
			await axios(
				postRequest(productsBulkImportUrl, formData, true, false, true)
			);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const clientsBulkImport = async (
	formData: FormData
): Promise<BulkImportResponse<Client>> => {
	try {
		const response: AxiosResponse<BulkImportResponse<Client>> = await axios(
			postRequest(clientsBulkImportUrl, formData, true, false, true)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const clientContactsBulkImport = async (
	formData: FormData
): Promise<BulkImportResponse<ClientContact>> => {
	try {
		const response: AxiosResponse<BulkImportResponse<ClientContact>> =
			await axios(
				postRequest(
					clientContactsBulkImportUrl,
					formData,
					true,
					false,
					true
				)
			);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const assessmentIssuesBulkImport = async (
	formData: FormData,
	currentActiveAssessmentId: string
): Promise<BulkImportResponse<AssessmentDataLog>> => {
	try {
		const response: AxiosResponse<BulkImportResponse<AssessmentDataLog>> =
			await axios(
				postRequest(
					assessmentsIssuesBulkImportUrl.replace(
						"{assessment_id}",
						currentActiveAssessmentId
					),
					formData,
					true,
					false,
					true
				)
			);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const qbrIssuesBulkImport = async (
	formData: FormData,
	currentActiveqbrId: string
): Promise<BulkImportResponse<HealthStatusDataLog>> => {
	try {
		const response: AxiosResponse<BulkImportResponse<HealthStatusDataLog>> =
			await axios(
				postRequest(
					qbrIssuesBulkImportUrl.replace(
						"{qbr_id}",
						currentActiveqbrId
					),
					formData,
					true,
					false,
					true
				)
			);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const opportunitiesBulkImportTemplateDownload = async (
	bulkImportTemplateFileType: BulkImportTemplateFileType
): Promise<Blob> => {
	try {
		const response: AxiosResponse<Blob> = await axios(
			getRequest(
				opportunitiesBulkImportTemplateDownloadUrl.replace(
					"{file_type}",
					bulkImportTemplateFileType
				),
				null,
				true,
				false,
				true
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const productsBulkImportTemplateDownload = async (
	bulkImportTemplateFileType: BulkImportTemplateFileType
): Promise<Blob> => {
	try {
		const response: AxiosResponse<Blob> = await axios(
			getRequest(
				productsBulkImportTemplateDownloadUrl.replace(
					"{file_type}",
					bulkImportTemplateFileType
				),
				null,
				true,
				false,
				true
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const clientsBulkImportTemplateDownload = async (
	bulkImportTemplateFileType: BulkImportTemplateFileType
): Promise<Blob> => {
	try {
		const response: AxiosResponse<Blob> = await axios(
			getRequest(
				clientsBulkImportTemplateDownloadUrl.replace(
					"{file_type}",
					bulkImportTemplateFileType
				),
				null,
				true,
				false,
				true
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const clientContactsBulkImportTemplateDownload = async (
	bulkImportTemplateFileType: BulkImportTemplateFileType
): Promise<Blob> => {
	try {
		const response: AxiosResponse<Blob> = await axios(
			getRequest(
				clientContactsBulkImportTemplateDownloadUrl.replace(
					"{file_type}",
					bulkImportTemplateFileType
				),
				null,
				true,
				false,
				true
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const assessmentsBulkImportTemplateDownload = async (
	bulkImportTemplateFileType: BulkImportTemplateFileType,
	repStyle: AssessmentRepresentationStyle
): Promise<Blob> => {
	try {
		const response: AxiosResponse<Blob> = await axios(
			getRequest(
				assessmentsIssuesBulkImportTemplateDownloadUrl
					.replace("{file_type}", bulkImportTemplateFileType)
					.replace("{assessment_representation_style}", repStyle),
				null,
				true,
				false,
				true
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const qbrBulkImportTemplateDownload = async (
	bulkImportTemplateFileType: BulkImportTemplateFileType,
	repStyle: QbrRepresentationStyle
): Promise<Blob> => {
	try {
		const response: AxiosResponse<Blob> = await axios(
			getRequest(
				qbrIssuesBulkImportTemplateDownloadUrl
					.replace("{file_type}", bulkImportTemplateFileType)
					.replace("{qbr_representation_style}", repStyle),
				null,
				true,
				false,
				true
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const downloadAllProductsCSV = async (): Promise<Blob> => {
	try {
		const response: AxiosResponse<Blob> = await axios(
			getRequest(downloadAllProductsCSVURL, null, true, false, true)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const downloadAllOpportunitiesCSV = async (): Promise<Blob> => {
	try {
		const response: AxiosResponse<Blob> = await axios(
			getRequest(downloadAllOpportunitiesCSVURL, null, true, false, true)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const downloadAllClientsCSV = async (): Promise<Blob> => {
	try {
		const response: AxiosResponse<Blob> = await axios(
			getRequest(downloadAllClientsCSVURL, null, true, false, true)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const downloadAllClientContactsCSV = async (): Promise<Blob> => {
	try {
		const response: AxiosResponse<Blob> = await axios(
			getRequest(downloadAllClientContactsCSVURL, null, true, false, true)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const downloadAllAssessmentIssuesCSV = async (
	assessmentId: string
): Promise<Blob> => {
	try {
		const response: AxiosResponse<Blob> = await axios(
			getRequest(
				downloadAllAssessmentIssuesCSVURL.replace(
					"{assessment_id}",
					assessmentId
				),
				null,
				true,
				false,
				true
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const downloadAllAssessmentMS365IssuesCSV = async (
	assessmentId: string
): Promise<Blob> => {
	try {
		const response: AxiosResponse<Blob> = await axios(
			getRequest(
				downloadAllAssessmentMS365IssuesCSVURL.replace(
					"{assessment_id}",
					assessmentId
				),
				null,
				true,
				false,
				true
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const downloadAllQbrIssuesCSV = async (qbrId: string): Promise<Blob> => {
	try {
		const response: AxiosResponse<Blob> = await axios(
			getRequest(
				downloadAllQbrIssuesCSVURL.replace("{qbr_id}", qbrId),
				null,
				true,
				false,
				true
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};
